import React from 'react';
import styled from 'styled-components';
import { Background, Border, Text } from 'styles/themeColors';
import { FontSize } from 'styles/themeSizes';
import { Space } from 'styles/themeSpaces';
import { Height } from 'styles/themeHeights';
import FlexboxContainer from '../FlexboxContainer';
import { TableColumn } from './GenericTable';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';

const HeaderWrapper = styled(FlexboxContainer)`
  background-color: ${Background.white};
  margin-bottom: 0;
  display: flex;
  flex-direction: row;
  width: 100%;
  min-width: 100%;
  position: sticky;
  top: 0;
  z-index: 10;   /* important so it stays above rows */
`;

const HeaderCell = styled.div<{ width?: string; headerStyle?: any; isFlexible?: boolean; sortable?: boolean; isCurrentSort?: boolean }>`
  padding: ${Space.sp2x} ${Space.spBase} ${Space.sp2x} ${Space.sp2x};
  text-align: left;
  background-color: ${Background.shaded};
  color: ${Text.primary};
  cursor: ${props => props.sortable ? 'pointer' : 'default'};
  font-weight: bold;
  font-size: ${FontSize.medium};
  line-height: 20px;
  border-right: 1px solid ${Border.light};
  min-height: ${Height.l};
  display: flex;
  align-items: center;
  justify-content: space-between;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  
  ${props => props.isFlexible
    ? `flex: 1; min-width: 120px;`
    : `width: ${props.width || 'auto'}; min-width: ${props.width || '120px'};`
  }

  &:first-child {
    border-left: 1px solid ${Border.light};
  }

  ${props => props.headerStyle && Object.entries(props.headerStyle).map(([key, value]) =>
    `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`
  ).join(' ')}
`;

const SortIcon = styled.div<{ isCurrentSort?: boolean }>`
  display: flex;
  align-items: center;
  margin-left: 8px;
  opacity: ${props => props.isCurrentSort ? 1 : 0};
  transition: opacity 0.2s ease;

  ${HeaderCell}:hover & {
    opacity: 1;
  }
`;

interface Props {
  columns: TableColumn[];
  hasDetailPanel?: boolean;
  onSortChange?: (columnIndex: number, direction: string) => void;
  sortBy?: any;
  enableSelection?: boolean;
  isAllSelected?: boolean;
  isIndeterminate?: boolean;
  onSelectAll?: () => void;
}

const GenericTableHeader = ({ columns, hasDetailPanel, onSortChange, sortBy, enableSelection, isAllSelected, isIndeterminate, onSelectAll }: Props) => {
  // Determine which columns should have fixed width vs flexible width
  const getColumnProps = (column: TableColumn) => {
    const title = column.title;

    // Orders table specific fixed widths
    const orderFixedWidths: Record<string, string> = {
      '#': '150px',
      'Status': '120px',
      'Date': '140px',
      'Payment': '120px',
      'Total amount': '140px'
    };

    // Check if this is an orders table column with specific width
    if (orderFixedWidths[title]) {
      return {
        width: orderFixedWidths[title],
        isFlexible: false
      };
    }

    // For tables, use column width if specified
    if (column.width && column.width !== 'auto') {
      // For percentage widths, make them flexible but constrained
      if (typeof column.width === 'string' && column.width.includes('%')) {
        return {
          width: column.width,
          isFlexible: true  // Changed to true for better responsiveness
        };
      }
      return {
        width: column.width,
        isFlexible: false
      };
    }

    // Default: flexible columns that will share remaining space
    return {
      width: 'auto',
      isFlexible: true
    };
  };

  const handleSort = (columnIndex: number, column: TableColumn) => {
    if (!onSortChange || !column.field) return;

    // Determine next sort direction
    const currentSort = sortBy?.field === column.field ? sortBy?.defaultSort : null;
    let nextDirection = 'asc';

    if (currentSort === 'asc') {
      nextDirection = 'desc';
    } else if (currentSort === 'desc') {
      nextDirection = 'asc';
    }

    onSortChange(columnIndex, nextDirection);
  };

  const getSortIcon = (column: TableColumn) => {
    const isCurrentSort = sortBy && sortBy.field === column.field;

    return (
      <SortIcon isCurrentSort={isCurrentSort}>
        {isCurrentSort && sortBy.defaultSort === 'asc' ? (
          <ArrowUpward style={{ fontSize: 16 }} />
        ) : isCurrentSort && sortBy.defaultSort === 'desc' ? (
          <ArrowDownward style={{ fontSize: 16 }} />
        ) : (
          <ArrowUpward style={{ fontSize: 16 }} />
        )}
      </SortIcon>
    );
  };

  return (
    <HeaderWrapper flexDirection="row" width="100%">
      {/* Selection checkbox header */}
      {enableSelection && (
        <HeaderCell width="40px" isFlexible={false} data-header-cell="true">
          <Checkbox
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            onChange={onSelectAll}
            size="medium"
          />
        </HeaderCell>
      )}
      {/* Empty header cell for expand/collapse column */}
      {hasDetailPanel && (
        <HeaderCell width="40px" isFlexible={false} data-header-cell="true">
          {/* Empty header for expand/collapse column */}
        </HeaderCell>
      )}

      {columns.map((column, index) => {
        const { width, isFlexible } = getColumnProps(column);
        const isSortable = !!column.field;
        const isCurrentSort = sortBy && sortBy.field === column.field;

        return (
          <HeaderCell
            key={index}
            width={width}
            isFlexible={isFlexible}
            data-header-cell="true"
            sortable={isSortable}
            isCurrentSort={isCurrentSort}
            headerStyle={column.headerStyle}
            onClick={() => isSortable && handleSort(index, column)}
          >
            <span>{column.title}</span>
            {isSortable && getSortIcon(column)}
          </HeaderCell>
        );
      })}
    </HeaderWrapper>
  );
};

export default GenericTableHeader;